import { extensionFor } from "./artifacts";
import type { Artifact, PortableArchive } from "./types";

export function downloadFile(name: string, content: string, type: string): void {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Some browsers start the download after the click handler returns.
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

const MARKER = /\[\[artifact:([0-9a-fA-F-]{36})\]\]/g;

/**
 * Artifact references are stored in messages as opaque markers. A Markdown
 * export is read outside the app, so each marker is replaced by the artifact's
 * actual content — otherwise the export would silently lose every document,
 * page and code file the conversation produced.
 */
function inlineArtifacts(content: string, artifacts: Map<string, Artifact>): string {
  return content.replace(MARKER, (whole, id: string) => {
    const artifact = artifacts.get(id.toLowerCase());
    if (!artifact) return whole;
    const fence = codeFenceFor(artifact.content);
    return `### ${artifact.title}\n\n${fence}${artifact.language}\n${artifact.content}\n${fence}`;
  });
}

function codeFenceFor(content: string): string {
  const longestRun = Math.max(
    0,
    ...Array.from(content.matchAll(/`+/g), (match) => match[0].length),
  );
  return "`".repeat(Math.max(3, longestRun + 1));
}

export function archiveToMarkdown(archive: PortableArchive): string {
  const artifacts = new Map(
    (archive.artifacts ?? []).map((artifact) => [artifact.id.toLowerCase(), artifact]),
  );
  const conversations = archive.conversations
    .map((conversation) => {
      const messages = conversation.messages
        .map((message) => {
          const meta = [
            message.model && `model: ${message.model}`,
            message.usage && `tokens: ${message.usage.totalTokens}`,
            message.usage?.cost !== undefined &&
              `cost: $${message.usage.cost.toFixed(6)}`,
          ]
            .filter(Boolean)
            .join(" · ");
          const body = inlineArtifacts(message.content, artifacts);
          return `## ${message.role === "user" ? "User" : "Assistant"}${
            meta ? ` — ${meta}` : ""
          }\n\n${body}`;
        })
        .join("\n\n---\n\n");
      return `# ${conversation.title}\n\n${messages}`;
    })
    .join("\n\n\n");

  // Artifacts created directly in the panel are never referenced by a message
  // and would otherwise be dropped from the export entirely.
  const referenced = new Set(
    archive.conversations.flatMap((conversation) =>
      conversation.messages.flatMap((message) =>
        [...message.content.matchAll(MARKER)].map((match) => match[1]!.toLowerCase()),
      ),
    ),
  );
  const orphans = (archive.artifacts ?? []).filter(
    (artifact) => !referenced.has(artifact.id.toLowerCase()),
  );
  const appendix = orphans.length
    ? `\n\n\n# Artifact\n\n${orphans
        .map((artifact) => {
          const fence = codeFenceFor(artifact.content);
          return `## ${artifact.title} (.${extensionFor(artifact.language)})\n\n${fence}${
            artifact.language
          }\n${artifact.content}\n${fence}`;
        })
        .join("\n\n")}`
    : "";

  return `# Vesper AI export\n\nExported: ${archive.exportedAt}\n\n${conversations}${appendix}`;
}

export function isPortableArchive(value: unknown): value is PortableArchive {
  if (!value || typeof value !== "object") return false;
  const archive = value as Partial<PortableArchive>;
  return (
    archive.format === "vesper-ai" &&
    // v1 archives predate multi-artifact support but are still importable.
    (archive.version === 1 || archive.version === 2) &&
    Array.isArray(archive.conversations) &&
    Array.isArray(archive.projects) &&
    Array.isArray(archive.assistants) &&
    Boolean(archive.settings)
  );
}
